import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { Settings, MessageSquare } from "lucide-react";
import { ChatWidget } from "../components/landing/ChatWidget";

const ShieldLogoLarge = () => (
  <svg width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M40 6L12 20V42C12 58.5 24.5 73.5 40 78C55.5 73.5 68 58.5 68 42V20L40 6Z"
      stroke="#D4AF37" strokeWidth="2.5" fill="rgba(212, 175, 55, 0.06)" />
    <path d="M24 42C24 42 31 32 40 32C49 32 56 42 56 42C56 42 49 52 40 52C31 52 24 42 24 42Z"
      stroke="#00FF41" strokeWidth="1.5" fill="none" opacity="0.8" />
    <circle cx="40" cy="42" r="6" stroke="#00FF41" strokeWidth="1.5" fill="rgba(0, 255, 65, 0.1)" />
    <circle cx="40" cy="42" r="3" fill="#00FF41" />
  </svg>
);

export default function MaintenancePage() {
  const [dots, setDots] = useState(".");

  useEffect(() => {
    const interval = setInterval(() => setDots(d => d.length >= 3 ? "." : d + "."), 700);
    return () => clearInterval(interval);
  }, []);

  return (
    <div data-testid="maintenance-page" className="min-h-screen relative overflow-hidden flex flex-col items-center justify-center" style={{ background: "#040F49" }}>
      {/* Background textures */}
      <div className="pointer-events-none absolute inset-0" style={{ backgroundImage: "linear-gradient(rgba(212,175,55,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(212,175,55,0.03) 1px, transparent 1px)", backgroundSize: "60px 60px" }} />
      <div className="pointer-events-none absolute rounded-full" style={{ top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: "700px", height: "700px", background: "radial-gradient(circle, rgba(212,175,55,0.07) 0%, transparent 70%)" }} />

      <div className="relative z-10 flex flex-col items-center text-center px-6 max-w-2xl">
        <motion.div initial={{ opacity: 0, scale: 0.7 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.9 }} className="mb-6">
          <ShieldLogoLarge />
        </motion.div>

        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }} style={{ color: "#D4AF37", fontSize: 11, fontFamily: "monospace", letterSpacing: "0.35em", textTransform: "uppercase", marginBottom: 12 }}>
          ARGUS XENIA
        </motion.p>

        <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.45 }} style={{ fontSize: "clamp(2rem, 6vw, 3.5rem)", fontWeight: 800, color: "#fff", lineHeight: 1.1, marginBottom: 20 }}>
          Coming <span style={{ color: "#D4AF37" }}>Soon</span>
        </motion.h1>

        <motion.div initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ delay: 0.6 }} style={{ width: 80, height: 1, background: "linear-gradient(90deg, transparent, #D4AF37, transparent)", marginBottom: 24 }} />

        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.75 }} style={{ color: "#94a3b8", fontSize: "clamp(0.9rem, 2vw, 1.05rem)", lineHeight: 1.7, marginBottom: 32, maxWidth: 480 }}>
          Argus Xenia is an AI Restaurant Operating System, currently in early access.<br />
          Chat with Argus below to learn more.
        </motion.p>

        {/* CENTER-STAGE: Argus Chat CTA */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9 }}
          style={{ marginBottom: 40, padding: "24px 32px", borderRadius: 16, background: "rgba(212,175,55,0.06)", border: "1px solid rgba(212,175,55,0.25)", maxWidth: 420, width: "100%", textAlign: "center" }}>
          <MessageSquare size={28} style={{ color: "#D4AF37", marginBottom: 12 }} />
          <p style={{ color: "#D4AF37", fontSize: 13, fontFamily: "monospace", letterSpacing: "0.2em", textTransform: "uppercase", marginBottom: 8 }}>
            Talk to Argus
          </p>
          <p style={{ color: "#94a3b8", fontSize: 14, lineHeight: 1.6 }}>
            Argus is the intelligence behind Argus Xenia. Ask it anything about the platform or your restaurant.
          </p>
        </motion.div>

        {/* Status */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.1 }} style={{ display: "inline-flex", alignItems: "center", gap: 12, background: "rgba(0,255,65,0.05)", border: "1px solid rgba(0,255,65,0.2)", borderRadius: 100, padding: "10px 20px", marginBottom: 40 }}>
          <span className="relative flex" style={{ width: 8, height: 8 }}>
            <span className="animate-ping absolute inline-flex rounded-full" style={{ background: "#00FF41", width: "100%", height: "100%", opacity: 0.75 }} />
            <span className="relative inline-flex rounded-full" style={{ background: "#00FF41", width: 8, height: 8 }} />
          </span>
          <span style={{ color: "#00FF41", fontFamily: "monospace", fontSize: 12, letterSpacing: "0.1em" }}>
            ARGUS ONLINE{dots}
          </span>
        </motion.div>

        {/* Feature pills */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.2 }} style={{ display: "flex", flexWrap: "wrap", gap: 8, justifyContent: "center" }}>
          {["Argus", "Echo (Calls)", "Apollo (Vision)", "Athena (SOPs)"].map(label => (
            <span key={label} style={{ background: "rgba(212,175,55,0.07)", border: "1px solid rgba(212,175,55,0.15)", borderRadius: 100, padding: "5px 12px", color: "#94a3b8", fontSize: 11, fontFamily: "monospace" }}>
              {label}
            </span>
          ))}
        </motion.div>
      </div>

      {/* Footer */}
      <div className="absolute bottom-6 left-0 right-0 flex justify-center">
        <p style={{ color: "rgba(148,163,184,0.25)", fontSize: 10, fontFamily: "monospace", letterSpacing: "0.2em", textTransform: "uppercase" }}>
          &copy; 2026 Argus Xenia
        </p>
      </div>

      {/* Admin gear */}
      <a href="/admin/login?redirect=/admin/command-center" data-testid="maintenance-admin-gear" title="Admin"
        style={{ position: "fixed", bottom: 20, right: 20, width: 32, height: 32, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.05)", color: "rgba(148,163,184,0.2)", zIndex: 50, textDecoration: "none" }}>
        <Settings size={14} />
      </a>

      {/* Argus Chat Widget — CENTER STAGE */}
      <ChatWidget agentType="argus" title="Talk to Argus" idleAutoOpen={true} idleTimeout={15000} />
    </div>
  );
}
